import React, { useState } from "react";

import * as Icon from "shared/icons/icons";

import type { ClearedEntry } from "./clearedByRefresh";
import { clearedRowNames } from "./clearedByRefresh";
import { ConflictAction, ConflictActions, TheirVersion } from "./reportStyled";
import { DecisionedBanner, NoticeDetail } from "./styled";

/**
 * All user-facing text for the notice lives here — copy is provisional until
 * the panel-states design pass lands, and a revision must be a one-file diff.
 */
const COPY = {
  lead: (rowNames: string) =>
    `A new screening run updated this review. Your entries were cleared ` +
    `because the underlying results changed: ${rowNames}.`,
  keep: "Copy anything you still need — this text isn't kept once dismissed.",
  view: "View what was cleared",
  hide: "Hide",
  findings: "Findings",
  actionForLeasing: "Action for leasing",
  internalNote: "Internal note",
  dismiss: "Dismiss",
};

/**
 * The auto-refresh cleared an analyst's entries and nothing else says so —
 * the refresh is deliberately promptless, so by the time this renders the
 * reset already happened. The banner's job is to name what was lost and hold
 * the cleared text long enough to copy back; the internal note exists nowhere
 * else in the system.
 */
export const RefreshClearedNotice = ({
  entries,
  onDismiss,
}: {
  entries: ClearedEntry[];
  onDismiss: () => void;
}) => {
  // collapsed by default: the notice announces on one line and preserves on
  // demand — expanded, a many-row reset must scroll inside its bound rather
  // than crush the checklist it pins above (design pass pending, PRODUCT-2059)
  const [expanded, setExpanded] = useState(false);
  return (
    <DecisionedBanner data-testid="vero1-refresh-cleared-notice">
      <Icon.WarningIcon />
      <div>
        <p>{COPY.lead(clearedRowNames(entries))}</p>
        {expanded && (
          <NoticeDetail data-testid="vero1-refresh-cleared-detail">
            {entries.map((entry) => {
              const kept = [
                [COPY.findings, entry.findings],
                [COPY.actionForLeasing, entry.actionForLeasing],
                [COPY.internalNote, entry.internalNote],
              ].filter(([, text]) => text);
              if (!kept.length) return null;
              return (
                <TheirVersion
                  key={entry.itemId}
                  data-testid="vero1-refresh-cleared-row"
                >
                  <p>
                    <strong>
                      {entry.applicantName
                        ? `${entry.label} — ${entry.applicantName}`
                        : entry.label}
                    </strong>
                  </p>
                  {kept.map(([label, text]) => (
                    <p key={label}>
                      {label}: {text}
                    </p>
                  ))}
                </TheirVersion>
              );
            })}
          </NoticeDetail>
        )}
        <p>{COPY.keep}</p>
        <ConflictActions>
          <ConflictAction
            type="button"
            data-testid="vero1-refresh-cleared-toggle"
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? COPY.hide : COPY.view}
          </ConflictAction>
          <ConflictAction
            type="button"
            data-testid="vero1-refresh-cleared-dismiss"
            onClick={onDismiss}
          >
            {COPY.dismiss}
          </ConflictAction>
        </ConflictActions>
      </div>
    </DecisionedBanner>
  );
};
